import { BreakpointObserver } from '@angular/cdk/layout';
import { formatDate } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    NgZone,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { saveAs } from 'file-saver';
import { debounceTime, take } from 'rxjs/operators';
import { PlanningHasEntity } from 'src/app/classes/planning-has-entity.class';
import { Planning } from 'src/app/classes/planning.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Features } from 'src/app/services/auth/features';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ChecklistSignOffService } from 'src/app/services/checklist-sign-off.service';
import { CodaltModusService } from 'src/app/services/codalt-modus.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { ToolboxService } from 'src/app/services/toolbox.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { environment } from 'src/environments/environment';
import { routes } from '../../app-routing.module';
import { AppRoute } from '../../interfaces/app-route.interface';
import { RouteOption } from '../../interfaces/route-option.interface';
import { AsphaltExportDialogComponent } from '../asphalt-export-dialog/asphalt-export-dialog.component';
import { AsphaltComponent } from '../asphalt/asphalt.component';
import { EntitiesOverviewComponent } from '../entities-overview/entities-overview.component';
import { HirePeopleDialogComponent } from '../hire-people-dialog/hire-people-dialog.component';
import { HiredCompaniesDialogComponent } from '../hired-companies-dialog/hired-companies-dialog.component';
import { HiringCompaniesDialogComponent } from '../hiring-companies-dialog/hiring-companies-dialog.component';
import { PastToolboxesDialogComponent } from '../past-toolboxes-dialog/past-toolboxes-dialog.component';
import { PdfBackgroundDialogComponent } from '../pdf-background-dialog/pdf-background-dialog.component';
import { PlanningListComponent } from '../planning-list/planning-list.component';
import { ProjectmanagerReportDialogComponent } from '../projectmanager-report-dialog/projectmanager-report-dialog.component';
import { ToolboxDialogComponent } from '../toolbox/toolbox-dialog/toolbox-dialog.component';
import { ToolboxComponent } from '../toolbox/toolbox.component';

@Component({
    selector: 'app-settings-menu',
    templateUrl: './settings-menu.component.html',
    styleUrls: ['./settings-menu.component.scss'],
    standalone: false,
})
export class SettingsMenuComponent extends CodaltComponent implements OnInit {
    @Output() public selectedTeams = new EventEmitter<number[]>();

    @Input() date: Date;
    @Input() projectId: string;
    @Input() showPlanningFilter = false;
    @Input() showHelpIcon = false;

    @Output() onOpenHelpClick = new EventEmitter<void>();

    @ViewChild('searchInput') searchInput: ElementRef<NgSelectComponent>;

    authenticatedUser: User;

    // Search functionality
    fcSearch = new FormControl();
    nonOfficeUsers: User[] = [];
    allUsers: User[] = [];
    searchOptions: RouteOption[];
    searchExpanded = false;
    searchLoading = false;
    mobile = false;
    open = false;
    location = location;
    viewModel = new HomeViewModel();
    codaltModusService = inject(CodaltModusService);
    checklistSignOffService = inject(ChecklistSignOffService);
    todayDate = formatDate(new Date(), 'yyyy-MM-dd', 'nl');

    constructor(
        public dialog: MatDialog,
        public dialogService: DialogService,
        private toolboxService: ToolboxService,
        private confirmDialogService: ConfirmDialogService,
        private breakpointObserver: BreakpointObserver,
        private planningService: PlanningService,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private userService: UserService,
        private apiService: ApiService,
        private ngZone: NgZone
    ) {
        super();
    }

    openHelp() {
        this.onOpenHelpClick.emit();
    }

    ngOnInit() {
        this.viewModel.updateViewStateMenu();

        this.authService.getUserDetails().subscribe(user => {
            this.authenticatedUser = user;
        });

        this.breakpointObserver.observe('(max-width: 1000px)').subscribe(match => {
            this.mobile = match.matches;
        });

        if (AuthorisationService.hasFunctionality(Functionalities.UserManagement)) {
            this.subscriptions.add(
                this.ngZone.onStable.pipe(take(1)).subscribe(() => {
                    this.subscriptions.add(
                        this.userService.getApiList(true, true, true, false).subscribe(users => {
                            this.allUsers = users;
                            this.nonOfficeUsers = users.filter(
                                user =>
                                    AuthorisationService.hasFunctionality(Functionalities.Plannable, user) &&
                                    !AuthorisationService.hasFunctionality(Functionalities.External, user)
                            );
                        })
                    );
                })
            );
        }

        // Subscribe to search input changes
        this.fcSearch.valueChanges.pipe(debounceTime(200)).subscribe(value => {
            if (!value || typeof value !== 'string') {
                this.searchOptions = [];
                return;
            }

            // Split value on spaces and trim each word
            const filterWords = value
                .split(' ')
                .map(word => word.trim().toLowerCase())
                .filter(word => word.length > 0);

            if (filterWords.length === 0) {
                this.searchOptions = [];
                return;
            }

            // Parse date from search value
            const searchValue = value.toLowerCase();
            let cleanedSearchValue = searchValue;

            // Search for year (2010-2059) in the search value, but not if followed by a dash
            const yearMatch = cleanedSearchValue.match(/20[1-5][0-9](?!-)/);

            // Remove found year from search value
            if (yearMatch) {
                cleanedSearchValue = cleanedSearchValue.replace(yearMatch[0], '').trim();
            }

            // Search for project ID format (1234-012)[DvdS], (123401)[TrackLine] and remove it from search value
            const projectIdMatch = searchValue.match(environment.projectIdentifier);
            const projectId = projectIdMatch ? projectIdMatch[1] : '';

            // Remove project ID from searchValue if found to avoid interference
            if (projectIdMatch) {
                cleanedSearchValue = cleanedSearchValue.replace(projectIdMatch[0], '').trim();
            }

            // Define month mappings - full names first, then abbreviations
            const monthMappings = {
                januari: 0,
                jan: 0,
                februari: 1,
                feb: 1,
                maart: 2,
                mar: 2,
                mrt: 2,
                april: 3,
                apr: 3,
                may: 4,
                mei: 4,
                juni: 5,
                jun: 5,
                juli: 6,
                july: 6,
                jul: 6,
                augustus: 7,
                aug: 7,
                september: 8,
                sep: 8,
                sept: 8,
                oktober: 9,
                okt: 9,
                oct: 9,
                november: 10,
                nov: 10,
                december: 11,
                dec: 11,
            };

            // Search for month in the search value
            let month = null; // Default to current month
            let day = 1; // Default to first day of month
            let monthFound = false;
            let foundMonthName = '';

            // Check for month names
            for (const [monthName, monthIndex] of Object.entries(monthMappings)) {
                if (cleanedSearchValue.includes(monthName)) {
                    month = monthIndex;
                    monthFound = true;
                    foundMonthName = monthName;
                    break;
                }
            }

            // Remove found month name from search value
            if (foundMonthName) {
                cleanedSearchValue = cleanedSearchValue.replace(foundMonthName, '').trim();
            }

            // If a month was found, look for a standalone number to use as day
            if (monthFound) {
                // Look for numbers that could be day (1-31) - not preceded or followed by digits
                const dayMatches = cleanedSearchValue.match(/(?<!\d)([1-9]|[12][0-9]|3[01])(?!\d)/g);
                if (dayMatches && dayMatches.length > 0) {
                    // Use the first valid day number found
                    const foundDay = parseInt(dayMatches[0], 10);
                    if (foundDay >= 1 && foundDay <= 31) {
                        day = foundDay;
                        // Remove found day from search value
                        cleanedSearchValue = cleanedSearchValue.replace(dayMatches[0], '').trim();
                    }
                }
            }

            // Check for number-month pattern (e.g., "1-5" means 1 mei)
            const dayMonthMatch = cleanedSearchValue.match(/(\d{1,2})-(\d{1,2})/);
            if (dayMonthMatch) {
                day = parseInt(dayMonthMatch[1], 10);
                month = parseInt(dayMonthMatch[2], 10) - 1; // Month is 0-indexed
                // Remove day-month pattern from search value
                cleanedSearchValue = cleanedSearchValue.replace(dayMonthMatch[0], '').trim();
            }

            // Split cleaned search value into words for user matching
            const searchWords = cleanedSearchValue.split(/\s+/).filter(word => word.length > 1);

            // Create result object for processing
            const currentYear = new Date().getFullYear();
            const result = {
                date: null as Date, // Format as YYYY-MM-DD
                search: cleanedSearchValue,
                searchWords,
                year: yearMatch
                    ? +yearMatch[0]
                    : monthFound
                      ? month < 6 && new Date().getMonth() > 9
                          ? currentYear + 1
                          : currentYear
                      : currentYear,
                period: null as number,
                week: null as number,
                projectId,
            };

            // Create date object and calculate period, set hour to 10 to prevent UTC / UTC+0200 issues
            const dateObj = month ? new Date(result.year, month, day, 10) : null;
            if (dateObj) {
                result.date = dateObj;
                const startOfYear = new Date(result.year, 0, 1);
                const daysDifference = Math.floor((dateObj.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
                result.period = Math.floor(daysDifference / 28) + 1; // 13 periods of 4 weeks (28 days)

                // Calculate Dutch week number using Utils.getWeek
                result.week = Utils.getWeek(dateObj);
            }

            // If no month found and no day-month pattern, look for standalone number to use as week/period
            if (!monthFound && !dayMonthMatch) {
                // Look for numbers between 1-53 in cleaned search value - not preceded or followed by digits
                const numberMatches = cleanedSearchValue.match(/(?<!\d)([1-9]|[1-4][0-9]|5[0-3])(?!\d)/g);
                if (numberMatches && numberMatches.length > 0) {
                    const foundNumber = parseInt(numberMatches[0], 10);

                    if (foundNumber > 13) {
                        // Numbers > 13 are always week numbers
                        result.week = foundNumber;
                        // Remove found number from search value
                        cleanedSearchValue = cleanedSearchValue.replace(numberMatches[0], '').trim();
                    } else {
                        // Numbers 1-13: check context
                        const hasWeekContext = searchValue.includes('week') || searchValue.includes('wk');
                        const hasPeriodContext = searchValue.includes('periode') || searchValue.includes('pv');

                        if (hasWeekContext) {
                            result.week = foundNumber;
                            // Remove week context words and number
                            cleanedSearchValue = cleanedSearchValue
                                .replace(/\bweek\b/g, '')
                                .replace(/\bwk\b/g, '')
                                .replace(numberMatches[0], '')
                                .trim();
                        } else if (hasPeriodContext) {
                            result.period = foundNumber;
                            // Remove period context words and number
                            cleanedSearchValue = cleanedSearchValue
                                .replace(/\bperiode\b/g, '')
                                .replace(/\bpv\b/g, '')
                                .replace(numberMatches[0], '')
                                .trim();
                        } else {
                            // No specific context, fill both
                            result.period = foundNumber;
                            result.week = foundNumber;
                            // Remove found number from search value
                            cleanedSearchValue = cleanedSearchValue.replace(numberMatches[0], '').trim();
                        }
                    }
                    if (result.week && !yearMatch && new Date().getMonth() < 3 && result.week > 30) {
                        result.year--;
                    }
                    if (result.period && !yearMatch && new Date().getMonth() < 5 && result.period > 8) {
                        result.year--;
                    }
                }
            }

            if (!result.date && result.week) {
                // Calculate the first Monday of the specified week using inverse of Utils.getWeek logic
                // January 4 is always in week 1 (ISO week standard)
                const jan4 = new Date(result.year, 0, 4, 10);
                // Find the Monday of week 1
                const week1Monday = Utils.getMonday(jan4);
                // Calculate the Monday of the target week
                const targetMonday = new Date(week1Monday);
                targetMonday.setDate(week1Monday.getDate() + (result.week - 1) * 7);
                targetMonday.setHours(10, 0, 0, 0); // Set hour to prevent UTC issues

                result.date = targetMonday;

                // Calculate period for this date
                const startOfYear = new Date(result.year, 0, 1);
                const daysDifference = Math.floor(
                    (targetMonday.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24)
                );
                result.period = Math.floor(daysDifference / 28) + 1;
            }

            const foundUserIds = new Set<number>();
            if (result.searchWords.length > 0) {
                // Find all users whose afas_employee_id matches any word in searchWords
                const matchedUsers = this.allUsers.filter(u =>
                    result.searchWords.some(word => u.afas_employee_id?.toLowerCase() === word.trim())
                );

                if (matchedUsers.length > 0) {
                    matchedUsers.forEach(user => foundUserIds.add(user.id));
                    // Remove all matched words from searchWords
                    const matchedWords = matchedUsers.map(u => u.afas_employee_id?.toLowerCase()).filter(Boolean);
                    result.searchWords = result.searchWords.filter(word => !matchedWords.includes(word.trim()));
                } else {
                    this.allUsers.forEach(user => {
                        if (user.name.replace(/\s+/g, ' ').match(new RegExp(`(^|\\s)${cleanedSearchValue}`, 'i'))) {
                            foundUserIds.add(user.id);
                        }
                    });
                }

                if (foundUserIds.size === 0) {
                    for (const word of [...result.searchWords].map(w => w.replace(/[^a-z]/gi, ''))) {
                        // Find users whose names contain the word at the beginning or after whitespace
                        const matchedUsers = this.allUsers.filter(user =>
                            user.name.match(new RegExp(`(^|\\s)${word}`, 'i'))
                        );

                        if (matchedUsers.length > 0) {
                            matchedUsers.forEach(user => foundUserIds.add(user.id));
                            cleanedSearchValue = cleanedSearchValue
                                .replace(new RegExp(`\\b${word}\\b`, 'gi'), '')
                                .trim();
                            result.searchWords = result.searchWords.filter(w => w !== word);
                        }
                    }
                }
            }

            const filteredNonOfficeUsers = this.nonOfficeUsers.filter(user => foundUserIds.has(user.id));
            const filteredAllUsers = this.allUsers.filter(user => foundUserIds.has(user.id));

            const processRoute = (r: AppRoute): RouteOption => {
                const routeObject: RouteOption = {
                    path: r.path,
                    origPath: r.path,
                    data: r.data,
                    user: r.user,
                };

                // Replace parameters and add corresponding properties
                if ((r.path.includes(':date') && result.date) || r.data.fallbackToday) {
                    const formattedDate = (result.date ?? new Date()).toISOString().split('T')[0];
                    routeObject.path = routeObject.path.replace(':date', formattedDate);
                    routeObject.date = result.date ?? new Date();
                }
                if (r.path.includes(':year') && result.year) {
                    routeObject.path = routeObject.path.replace(':year', result.year.toString());
                    routeObject.year = result.year;
                }
                if (r.path.includes(':period') && result.period) {
                    routeObject.path = routeObject.path.replace(':period', result.period.toString()).toString();
                    routeObject.period = result.period;
                }
                if (r.path.includes(':week') && result.week) {
                    routeObject.path = routeObject.path.replace(':week', result.week.toString());
                    routeObject.week = result.week;
                }
                if (r.path.includes(':project') && result.projectId) {
                    routeObject.path = routeObject.path.replace(':project', result.projectId);
                    routeObject.projectId = result.projectId;
                }

                return routeObject;
            };

            const filteredRoutes = routes
                .filter(r => {
                    const functionalityAllowed =
                        !r.data?.requiredFunctionality ||
                        AuthorisationService.hasAnyFunctionality(r.data.requiredFunctionality);
                    const tokenAllowed = r.path.indexOf(':token') === -1;

                    return functionalityAllowed && tokenAllowed && r.data?.searchName && !r.data.disabled;
                })
                .map(route => {
                    return {
                        path: route.path,
                        data: route.data,
                    } as {
                        path: string;
                        data?: any;
                        user?: User;
                    };
                });

            const matchedRoutes = filteredRoutes
                .filter(r => {
                    if (result.search?.length === 0) {
                        return false;
                    }

                    const pathMatch = result.searchWords.some(word => r.path.includes(word));
                    const dataSearchMatch =
                        Array.isArray(r.data?.searchWords) &&
                        result.searchWords.some(word => r.data.searchWords.some((ds: string) => ds.includes(word)));

                    return pathMatch || dataSearchMatch;
                })
                .map(processRoute);

            const withUserIdRoutes = (
                matchedRoutes.length > 0 ? matchedRoutes : projectIdMatch ? [] : filteredRoutes.map(processRoute)
            )
                .filter(r => r.path.includes(':userId'))
                .flatMap(route =>
                    (route.data.allUsers ? filteredAllUsers : filteredNonOfficeUsers).map(user => {
                        return {
                            ...route,
                            path: route.path.replace(':userId', `${user.id}`),
                            user,
                        } as RouteOption;
                    })
                );

            const withDateRoutes = (matchedRoutes.length > 0 ? [] : filteredRoutes)
                .filter(r => r.path.includes(':date') || r.path.includes(':year'))
                .map(processRoute);
            const useredRoutes = [
                ...matchedRoutes,
                ...withUserIdRoutes,
                ...filteredRoutes.filter(r => r.path.includes(':project')).map(processRoute),
                ...withDateRoutes,
            ].filter(r => !r.path.match(/\/:[a-z]+/i));

            const applyUseredRoutes = (useredRoutes: RouteOption[]) => {
                // Filter out routes when more specific versions exist
                this.searchOptions = useredRoutes
                    .filter(
                        route =>
                            !useredRoutes.some(
                                other => other.path !== route.path && other.path.startsWith(route.path + '/')
                            )
                    )
                    .sort((a, b) => {
                        const getSortKey = (route: RouteOption) => {
                            const paramCount = Array.from(route.origPath.matchAll(/\/:/g)).length;
                            const sortName = route.data?.sortName ? route.data?.sortName(route) : 'z';
                            const searchName = route.data?.searchName ? route.data?.searchName(route) : '';

                            return `${route.user?.name || route.path.includes('planning') ? 'yyyyyyy' : 'zzzzzzz'}|${sortName}|${9 - paramCount}|${searchName}`;
                        };

                        return getSortKey(a).localeCompare(getSortKey(b));
                    });
                this.searchLoading = false;
            };

            if (projectId?.length > 0 && AuthorisationService.hasFeature(Features.PlanningSearch)) {
                this.planningService.searchGlobal(projectId).subscribe(plannings => {
                    if (result.searchWords.length > 0) {
                        plannings = plannings.filter((planning: Planning & { main_planning?: PlanningHasEntity }) => {
                            const searchablePlanningText = [
                                planning.location,
                                planning.main_planning?.location,
                                planning.comment,
                            ]
                                .filter(Boolean)
                                .join(' ')
                                .toLowerCase();
                            return result.searchWords.every(word => searchablePlanningText.includes(word));
                        });
                    }

                    useredRoutes.push(...this.mapPlanningsToRoutes(plannings));

                    applyUseredRoutes(useredRoutes);
                });
            } else {
                applyUseredRoutes(useredRoutes);
            }
        });
    }

    navigateSearch(text: MatAutocompleteSelectedEvent) {
        try {
            this.router.navigateByUrl(text.option.value.path);
            this.onSearchBlur();
        } catch (e) {
            console.error(e);
        }
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (!this.viewModel.search) {
            return;
        }

        if (
            event.ctrlKey &&
            event.shiftKey &&
            event.key.toLowerCase() === 'f' &&
            document.activeElement?.id !== 'global-search'
        ) {
            this.onSearchFocus();
            event.preventDefault();
        }

        if (event.key === 'Escape') {
            this.onSearchBlur();
            (document.querySelector('.logo') as HTMLElement)?.focus();
            event.preventDefault();
            event.stopPropagation();
        }
    }

    logout() {
        this.confirmDialogService
            .confirm({
                title: 'Uitloggen',
                text: 'Weet je zeker dat je wilt uitloggen?',
                confirm: 'Uitloggen',
                cancel: 'Hier blijven',
            })
            .then(
                () => {
                    this.router.navigateByUrl('/logout');
                },
                () => {}
            );
    }

    toggleCodaltMode() {
        this.codaltModusService.toggle();
    }

    openPdfBackgroundDialog() {
        this.dialogService.open(PdfBackgroundDialogComponent, null, {
            width: '860px',
            maxWidth: '95vw',
            minWidth: '0px',
            minHeight: 'auto',
        });
    }

    downloadSqlFile() {
        this.apiService.getBlobCall(`codalt/download-database`).subscribe(data => {
            saveAs(
                data,
                `${environment.shortName}-db-dump-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm-ss', 'nl')}.sql.7z`
                    .toLowerCase()
                    .replaceAll(' ', '')
            );
        });
    }
    closeMobileMenu() {
        this.open = false;
        document.body.click();
    }

    // So the "Niet-verwerkte dagrapporten" link can carry over the customer you're already
    // looking at (e.g. from the productie-tabel or declaraties pages) as a filter.
    customerIdFromUrl(): number | null {
        return Number(this.route.snapshot.paramMap.get('customerId')) || null;
    }

    openProfile() {
        if (AuthorisationService.hasFunctionality(Functionalities.AfasAuth)) {
            this.router.navigateByUrl('/profiel');
        }
    }

    exportAsphalt() {
        this.dialogService.open(AsphaltExportDialogComponent);
    }

    projectmanagerReport() {
        this.dialogService.open(ProjectmanagerReportDialogComponent, null, { minHeight: '300px' });
    }

    planningList() {
        this.dialogService.open(PlanningListComponent);
    }

    entities() {
        this.dialogService.open(EntitiesOverviewComponent);
    }

    asphalt() {
        this.dialogService.open(AsphaltComponent);
    }

    hirePeople() {
        this.dialogService.open(HirePeopleDialogComponent);
    }

    hiringCompanies() {
        this.dialogService.open(HiringCompaniesDialogComponent);
    }

    hiredCompanies() {
        this.dialogService.open(HiredCompaniesDialogComponent);
    }

    showToolbox() {
        this.subscriptions.add(
            this.toolboxService.getCurrentToolbox().subscribe(toolbox => {
                if (toolbox.data) {
                    this.dialog.open(ToolboxDialogComponent, {
                        panelClass: 'toolbox-dialog',
                        width: '99vw',
                        maxWidth: '1300px',
                        height: '99vh',
                        minHeight: '500px',
                        maxHeight: '99vh',
                        disableClose: true,
                        data: toolbox.data,
                    });
                } else {
                    this.confirmDialogService.confirm({
                        title: 'Toolbox',
                        text: 'Er is momenteel geen toolbox beschikbaar.',
                        confirm: 'Sluiten',
                        cancel: null,
                    });
                }
            })
        );
    }

    showPastToolboxes() {
        this.dialogService.open(PastToolboxesDialogComponent);
    }

    toolbox() {
        this.dialogService.open(ToolboxComponent, null, {
            minWidth: '700px',
            maxHeight: '99vh',
        });
    }

    onSearchFocus() {
        this.searchExpanded = true;

        setTimeout(() => {
            this.searchInput?.nativeElement.focus();
        });
    }

    prefillSearch(text: string) {
        this.fcSearch.setValue(text);

        this.onSearchFocus();
    }

    onSearchBlur() {
        this.searchExpanded = false;

        setTimeout(() => {
            if (!this.searchExpanded) {
                this.searchExpanded = false;
                this.searchInput.nativeElement.blur();
                this.fcSearch.reset();
                this.searchOptions = [];
            }
        }, 250);
    }

    mapPlanningsToRoutes(plannings: Planning[]): RouteOption[] {
        return plannings.map((planning: Planning & { main_planning: PlanningHasEntity }) => {
            const {
                main_planning: { begindate, enddate, entitytype_key },
                afas_project_id,
                id,
                status_key,
            } = planning;
            const { rootPage, rootPageName, entitytypes, asphaltPlanning } = environment;

            const BEHOEFTE_STATUS_KEY = this.environment.status.Planning.PlanningBehoefte;

            const isBehoefte = !begindate && status_key === BEHOEFTE_STATUS_KEY;

            const beginDate = Utils.formatDate(begindate, "EEE d MMM ''yy HH:mm");
            const endDate = Utils.formatDate(enddate, 'HH:mm');
            const week = Utils.getWeek(new Date(begindate));
            const year = new Date(begindate).getFullYear();
            const pathDate = asphaltPlanning
                ? Utils.formatDate(begindate)
                : isBehoefte
                  ? 'behoefte'
                  : `${week}/${year}`;

            const subtitle = isBehoefte
                ? `Behoefte - ${afas_project_id}`
                : `${beginDate}-${endDate} - ${afas_project_id}`;
            const path = `${rootPage}/${pathDate}/${id}`;

            return {
                path,
                origPath: path,
                planning,
                sort: Utils.formatDate(begindate, 'yyyyMMdd'),
                data: { searchName: () => `${rootPageName} ${entitytypes[entitytype_key].name}` },
                subtitle,
            };
        });
    }
}

export class HomeViewModel {
    onlyCodalt = false;
    codaltMode = false;
    manageUsers = false;
    manageEntities = false;
    manageAsphalt = false;
    manageHiringCompanies = false;
    manageToolbox = false;
    manageProjects = false;
    manageClients = false;
    manageMenu = false;
    manageTickets = false;
    manageChecklists = false;
    manageArticles = false;
    manageDeclarations = false;
    manageMaterials = false;
    aiReports = false;
    yearPlanning = false;
    behoefteOverview = false;

    checkHours = false;
    generalHourCheck = false;
    projectWeekReport = false;
    projectDayReport = false;
    checklists = false;

    settlementsAndAfas = false;
    leave = false;
    hoursMenu = false;

    planningMenu = false;
    exportAsphalt = false;
    reportHiringCompanies = false;
    inventarizePlanning = false;
    planningList = false;
    hirePeople = false;
    projectmanagerReport = false;
    exportMenu = false;

    hourRegistration = false;
    periodSettlement = false;
    tickets = false;
    toolbox = false;

    vkm = false;
    asphaltTeams = false;
    employeePlanning = false;
    transportPlanning = false;
    asphaltPlanning = false;
    staffing = false;
    personalSchedule = false;
    executorsPlanning = false;
    search = false;
    orderdirect = false;
    pdfApplyBackgroundSave = false;

    updateViewStateMenu() {
        this.onlyCodalt = AuthorisationService.hasRole(environment.roles.Codalt);
        this.manageUsers = AuthorisationService.hasFunctionality(Functionalities.UserManagement);
        this.manageEntities = AuthorisationService.hasAnyFunctionality([
            Functionalities.EntitiesManagement,
            Functionalities.VkmManagement,
        ]);
        this.manageAsphalt = AuthorisationService.hasFunctionality(Functionalities.AsphaltManagement);
        this.manageHiringCompanies = AuthorisationService.hasFunctionality(Functionalities.HiringManagement);
        this.manageToolbox = AuthorisationService.hasFunctionality(Functionalities.ToolboxManagement);
        this.manageProjects = AuthorisationService.hasFunctionality(Functionalities.ProjectManagement);
        this.manageClients = AuthorisationService.hasFunctionality(Functionalities.ClientManagement);
        this.manageTickets = AuthorisationService.hasFunctionality(Functionalities.TicketsManagement);
        this.manageChecklists = AuthorisationService.hasFunctionality(Functionalities.ChecklistManagement);
        this.manageArticles = AuthorisationService.hasFunctionality(Functionalities.ArticleManagement);
        this.manageDeclarations = AuthorisationService.hasFunctionality(Functionalities.DeclarationManagement);
        this.manageMaterials = AuthorisationService.hasFunctionality(Functionalities.MaterialsManagement);
        this.manageMenu = AuthorisationService.hasAnyFunctionality([
            Functionalities.UserManagement,
            Functionalities.EntitiesManagement,
            Functionalities.AsphaltManagement,
            Functionalities.HiringManagement,
            Functionalities.ToolboxManagement,
            Functionalities.ProjectManagement,
            Functionalities.ClientManagement,
            Functionalities.TicketsManagement,
            Functionalities.ChecklistManagement,
            Functionalities.MaterialsManagement,
            Functionalities.ArticleManagement,
            Functionalities.DeclarationManagement,
        ]);

        this.vkm =
            environment.vkmPlanning &&
            AuthorisationService.hasAnyFunctionality([Functionalities.VkmRead, Functionalities.VkmReadWithPrice]);
        this.asphaltTeams = environment.asphaltPlanning && [353, 1, 22, 244].includes(LocalStorage.user?.id);
        this.employeePlanning =
            environment.employeePlanning && AuthorisationService.hasFunctionality(Functionalities.EmployeePlanningRead);
        this.transportPlanning =
            environment.transportPlanning && AuthorisationService.hasFunctionality(Functionalities.Transportplanning);
        this.asphaltPlanning = AuthorisationService.hasAnyFunctionality([
            Functionalities.PlanningOverviewOwn,
            Functionalities.PlanningOverview,
            Functionalities.PlanningOverviewManagement,
        ]);
        this.staffing = AuthorisationService.hasFunctionality(Functionalities.Staffing);
        this.personalSchedule = AuthorisationService.hasFunctionality(Functionalities.OwnHours);
        this.inventarizePlanning =
            environment.inventarizePlanning && AuthorisationService.hasFunctionality(Functionalities.PlanningOverview);
        this.executorsPlanning =
            environment.executorPlanning &&
            AuthorisationService.hasAnyFunctionality([
                Functionalities.ExecutorPlanning,
                Functionalities.ExecutorPlanningRead,
            ]);
        this.yearPlanning = environment.weekPlanning && this.asphaltPlanning;
        this.behoefteOverview =
            environment.weekPlanning && AuthorisationService.hasFunctionality(Functionalities.PlanningOverview);
        this.planningMenu =
            this.inventarizePlanning ||
            this.vkm ||
            this.asphaltTeams ||
            this.employeePlanning ||
            this.transportPlanning ||
            this.asphaltPlanning ||
            this.staffing ||
            this.personalSchedule ||
            this.executorsPlanning;

        this.checkHours = AuthorisationService.hasAnyFunctionality([
            Functionalities.HourCheck,
            Functionalities.HourCheckRestricted,
            Functionalities.HourTimeEdit,
        ]);
        this.generalHourCheck = AuthorisationService.hasFunctionality(Functionalities.CommonHourCheck);
        this.projectWeekReport = AuthorisationService.hasFunctionality(Functionalities.Weekreports);
        this.projectDayReport =
            environment.dayReportPlanning && AuthorisationService.hasFunctionality(Functionalities.Weekreports);
        this.projectWeekReport = AuthorisationService.hasFunctionality(Functionalities.Weekreports);
        this.settlementsAndAfas = AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution);
        this.leave = AuthorisationService.hasAnyFunctionality([Functionalities.Leave, Functionalities.LeaveManagement]);
        this.hoursMenu =
            this.checkHours ||
            this.generalHourCheck ||
            this.projectWeekReport ||
            this.projectDayReport ||
            this.settlementsAndAfas ||
            this.leave;

        this.exportAsphalt = AuthorisationService.hasFunctionality(Functionalities.AsphaltManagement);
        this.hirePeople = AuthorisationService.hasFunctionality(Functionalities.HiringManagement);
        this.reportHiringCompanies = AuthorisationService.hasFunctionality(Functionalities.TransportplanningManagement);
        this.projectmanagerReport = AuthorisationService.hasFunctionality(Functionalities.Report);
        this.checklists = AuthorisationService.hasAnyFunctionality([
            Functionalities.Checklist,
            Functionalities.ChecklistSignOff,
        ]);
        this.exportMenu = AuthorisationService.hasAnyFunctionality([
            Functionalities.AsphaltManagement,
            Functionalities.HiringManagement,
            Functionalities.TransportplanningManagement,
            Functionalities.ProjectManagement,
            Functionalities.Checklist,
            Functionalities.ChecklistSignOff,
            Functionalities.CommonOffice,
            Functionalities.DeclarationManagement,
        ]);

        this.hourRegistration = AuthorisationService.hasFunctionality(Functionalities.Hours);
        this.periodSettlement =
            AuthorisationService.hasFunctionality(Functionalities.ReadPeriodSettlement) &&
            AuthorisationService.hasFunctionality(Functionalities.Plannable);
        this.tickets = AuthorisationService.hasFunctionality(Functionalities.Tickets);
        this.toolbox = AuthorisationService.hasFunctionality(Functionalities.Toolbox);
        this.search =
            AuthorisationService.hasFunctionality(Functionalities.UserManagement) &&
            AuthorisationService.hasAnyRole([environment.roles.Planner, environment.roles.Hrm]);
        this.aiReports = AuthorisationService.hasFunctionality(Functionalities.AiReports);
        this.orderdirect = environment.orderDirect && AuthorisationService.hasFeature(Features.OrderdirectInvoices);
        this.pdfApplyBackgroundSave = AuthorisationService.hasFeature(Features.PdfApplyBackgroundSave);
        this.codaltMode = AuthorisationService.hasFunctionality(Functionalities.CodaltMode);
    }
}
